import { ModuleDefinition, PageDefinition } from '@/cmsBackend/transformers/page/commonPage';
import { BREADCRUMBS_POSITION_OPTIONS } from '@/common/constants/module';
import { useTheme } from '@/common/contexts/ContextProvider';
import { Env, Theme } from '@/common/types';
import { Module } from '@/components/Module';
import type { HeaderCreatorVariant } from '@/bsp/config';
import dynamic from 'next/dynamic';
import React, { useEffect } from 'react';

const NavbarComponent = dynamic(
  () =>
    import(/* webpackChunkName: "navbar_component" */ '@/components/global').then(
      (mod) => mod.Navbar,
    ),
  { ssr: true, loading: () => null },
);

const FooterComponent = dynamic(
  () =>
    import(/* webpackChunkName: "footer_component" */ '@/components/global').then(
      (mod) => mod.Footer,
    ),
  { ssr: true },
);

export interface OrganizerContainerProps {
  env: Env;
  pageData: PageDefinition;
  creatorVariant?: HeaderCreatorVariant;
  isUserAuthenticated?: boolean;
  /** See {@link NavbarProps.onCtaClick}. */
  onCtaClick?: (_cta: string, _destination: string) => void;
}

/**
 * OrganizerContainer that is responsible for Rendering /organizer/* Page UI
 */
export const OrganizerContainer: React.FC<OrganizerContainerProps> = ({
  pageData,
  env,
  creatorVariant,
  isUserAuthenticated,
  onCtaClick,
}) => {
  const { setPageTheme } = useTheme();

  // Update theme context
  useEffect(() => {
    if (pageData.theme) {
      setPageTheme(pageData.theme as Theme);
    }
  }, [pageData.theme, setPageTheme]);

  return (
    <>
      {pageData.include_navbar && pageData.navigation && (
        <NavbarComponent
          {...pageData.navigation}
          creatorVariant={creatorVariant}
          isUserAuthenticated={isUserAuthenticated}
          onCtaClick={onCtaClick}
        />
      )}

      <main id="main-content">
        {pageData.modules.map((module: ModuleDefinition, index: Number) => {
          // This is common snippet to loop over Modules Content array
          const key = `${module.type}__${index}`;
          return (
            <Module type={module.type} value={module.value} key={key} isFirstModule={index === 0} />
          );
        })}
      </main>

      {pageData.include_footer && (
        <FooterComponent
          env={env}
          marketingSectionData={pageData.marketingFooter}
          breadcrumbs={
            pageData.breadcrumbs?.position === BREADCRUMBS_POSITION_OPTIONS.PAGE_BOTTOM
              ? pageData.breadcrumbs
              : undefined
          }
        />
      )}
    </>
  );
};
